
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Send, Image, Mic, X } from 'lucide-react';
import { useSimpleAudioRecorder } from '@/hooks/useSimpleAudioRecorder';
import { useMessageLimits } from '@/hooks/useMessageLimits';
import { MessageLimitIndicator } from './MessageLimitIndicator';
import PlanLimitModal from './PlanLimitModal';
import { PixWarningModal } from './PixWarningModal';
import { ProfileRequirementsModal } from './ProfileRequirementsModal';
import { ReplyingToMessage } from '@/types/chat';
import { useAuth } from '@/hooks/useAuth';
import { useChatAccess } from '@/hooks/useChatAccess';
import { pixValidator } from '@/utils/pixValidation';
import { useIsMobile } from '@/hooks/use-mobile';

interface UltraSimpleChatInputProps {
  onSendText: (content: string) => Promise<boolean>;
  onSendImage: (file: File) => Promise<boolean>;
  onSendAudio: (audioBlob: Blob, duration: number) => Promise<boolean>;
  replyingTo?: ReplyingToMessage | null;
  onCancelReply?: () => void;
  externalValue?: string;
  onExternalValueClear?: () => void;
}

export const UltraSimpleChatInput: React.FC<UltraSimpleChatInputProps> = ({
  onSendText,
  onSendImage,
  onSendAudio,
  replyingTo,
  onCancelReply,
  externalValue,
  onExternalValueClear
}) => {
  const { profileData } = useAuth();
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPixWarning, setShowPixWarning] = useState(false);
  const [showProfileRequirementsModal, setShowProfileRequirementsModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { 
    currentCount, 
    limit, 
    remaining, 
    canSend: canSendByLimit,
    isLoading, 
    hoursUntilReset, 
    refreshLimits 
  } = useMessageLimits();

  const { allowed: canChat, missingRequirements } = useChatAccess({ context: 'list' });

  // Verificação agora é apenas informativa (banners exibem o status)
  // Não bloqueia mais o envio de mensagens
  const canSend = canChat && canSendByLimit;

  const {
    isRecording,
    audioRecording,
    startRecording,
    stopRecording,
    cancelRecording
  } = useSimpleAudioRecorder();

  const checkLimitAndExecute = async (action: () => Promise<boolean>) => {
    // 1. Verificar requisitos do perfil primeiro
    if (!canChat) {
      setShowProfileRequirementsModal(true);
      return false;
    }

    // 2. Verificar limites diários
    if (!canSendByLimit) {
      setShowLimitModal(true);
      return false;
    }

    setIsSending(true);
    try {
      const success = await action();
      if (success) {
        refreshLimits(); // Atualizar contador após envio bem-sucedido
      }
      return success;
    } finally {
      setIsSending(false);
    }
  };

  // Função para ajustar altura automaticamente
  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const scrollHeight = inputRef.current.scrollHeight;
      const lineHeight = 24; // aproximadamente 1.5rem
      const maxHeight = lineHeight * 5; // 5 linhas máximo
      const newHeight = Math.min(scrollHeight, maxHeight);
      inputRef.current.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  // Handle external value from icebreaker suggestions
  useEffect(() => {
    if (externalValue) {
      setInputValue(externalValue);
      onExternalValueClear?.();
      // Focus input after setting value
      setTimeout(() => {
        inputRef.current?.focus();
        adjustTextareaHeight();
      }, 100);
    }
  }, [externalValue, onExternalValueClear]);

  const handleSendText = async () => {
    if (!inputValue.trim()) return;
    
    // Verificar se há chave PIX na mensagem
    if (pixValidator.detectarPixNoTexto(inputValue)) {
      setShowPixWarning(true);
      return;
    }
    
    const success = await checkLimitAndExecute(() => onSendText(inputValue));
    if (success) {
      setInputValue('');
      // Voltar foco para o input após envio
      setTimeout(() => {
        inputRef.current?.focus();
        adjustTextareaHeight();
      }, 0);
    }
  };

  const handleSendImage = async (file: File) => {
    await checkLimitAndExecute(() => onSendImage(file));
  };

  const handleSendAudio = async () => {
    if (!audioRecording?.url) return;
    
    const response = await fetch(audioRecording.url);
    const audioBlob = await response.blob();
    
    const success = await checkLimitAndExecute(() => onSendAudio(audioBlob, audioRecording.duration || 0));
    if (success) {
      cancelRecording();
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleSendImage(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isInputDisabled = !canSend || isSending;
  const placeholder = !canChat
    ? "Complete seu perfil para usar o chat"
    : !canSendByLimit 
      ? "Limite de mensagens atingido - Faça upgrade!"
      : "Digite sua mensagem...";

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:left-80 lg:left-80 bg-black" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Indicador de limite */}
      <div className="bg-black border-b border-white/10">
        <div className="w-full px-3">
          <MessageLimitIndicator
            currentCount={currentCount}
            limit={limit}
            remaining={remaining}
            isLoading={isLoading}
            planType={profileData?.plan_type || 'sponsor_free'}
          />
        </div>
      </div>

      <div className="bg-black border-t border-white/10 py-4">
        <div className="w-full px-3">
        {/* Reply Preview */}
        {replyingTo && (
          <div className="mb-3 p-2 bg-white/5 rounded-lg border-l-2 border-luxury-red">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-gray-400">
                  {isMobile ? `Para ${replyingTo.sender_name}` : `Respondendo para ${replyingTo.sender_name}`}
                </p>
                <p className="text-sm text-gray-200 truncate">{replyingTo.content}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelReply}
                className="p-1 h-auto text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Audio Recording Preview */}
        {audioRecording?.url && (
          <div className="mb-3 p-3 bg-white/5 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-luxury-red" />
                <span className="text-sm text-gray-200">
                  Áudio gravado ({Math.round(audioRecording.duration || 0)}s)
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelRecording}
                  className="text-gray-400 hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSendAudio}
                  disabled={isInputDisabled}
                  className="bg-luxury-red hover:bg-luxury-red/90"
                >
                  Enviar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Input Area - sempre funcional (verificação é apenas informativa via banners) */}
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder}
              disabled={isInputDisabled}
              rows={1}
              className={`flex-1 bg-transparent border-none text-white placeholder-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none resize-none overflow-hidden ${
                !canSend ? 'text-red-400' : ''
              }`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendText();
                }
              }}
              style={{ 
                minHeight: '24px',
                lineHeight: '24px'
              }}
            />
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isInputDisabled}
              className="p-2 h-auto text-gray-400 hover:text-white"
            >
              <Image className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isInputDisabled}
              className={`p-2 h-auto ${
                isRecording 
                  ? 'text-red-400 hover:text-red-300' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Mic className="h-4 w-4" />
            </Button>
          </div>

          <Button
            onClick={handleSendText}
            disabled={isInputDisabled || !inputValue.trim()}
            className={`rounded-full p-3 ${
              canSend 
                ? 'bg-luxury-red hover:bg-luxury-red/90' 
                : 'bg-gray-600 cursor-not-allowed'
            }`}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        </div>
      </div>

      {/* Modal de Limite */}
      <PlanLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        currentCount={currentCount}
        limit={limit}
        planType={profileData?.plan_type || 'sponsor_free'}
      />

      {/* Modal de Aviso PIX */}
      <PixWarningModal
        isOpen={showPixWarning}
        onClose={() => setShowPixWarning(false)}
      />

      {/* Modal de Requisitos do Perfil */}
      <ProfileRequirementsModal
        isOpen={showProfileRequirementsModal}
        onClose={() => setShowProfileRequirementsModal(false)}
        missingRequirements={missingRequirements}
      />
    </div>
  );
};
